import { KeyboardArrowLeft, Settings } from "@mui/icons-material";
import {
  Box,
  Button,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  Option,
  Select,
  Skeleton,
  Stack,
  ToggleButtonGroup,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format, parse } from "date-fns";
import { useMemo } from "react";

import { useMeProfileQuery } from "@/hooks/profile";

import { useCurrentVenueUuid, useVenueStaffQuery } from "../../hooks/venues";
import { StaffItem } from "../forms/StaffSelect";
import { IconButtonLink } from "../Links";
import TaskSetMenuItems from "../SetCalendar/TaskSetMenuItems";
import { useTaskSetOverview } from "./useTaskSetOverview";

const AssigneeSelector = () => {
  const { taskSet, selectedAssignee, setSelectedAssignee } = useTaskSetOverview();
  const currentVenueUuid = useCurrentVenueUuid();
  const { data: staff } = useVenueStaffQuery(currentVenueUuid);
  const { data: meProfile } = useMeProfileQuery();

  const assigneeChoices = useMemo(() => {
    const assignees = new Map();
    taskSet?.tasks?.forEach((task) => {
      task.assignees?.forEach((assignee) => {
        if (assignees.has(assignee.uuid)) return;
        assignees.set(assignee.uuid, assignee);
      });
    });
    staff?.forEach((staffInfo) => {
      if (assignees.has(staffInfo.user.uuid) || !staffInfo.isActive) return;
      assignees.set(staffInfo.user.uuid, staffInfo.user);
    });
    if (meProfile && assignees.has(meProfile.uuid)) {
      const meAtTop = new Map();
      meAtTop.set(meProfile.uuid, meProfile);
      assignees.forEach((value, key) => {
        if (key !== meProfile.uuid) {
          meAtTop.set(key, value);
        }
      });
      return meAtTop;
    }
    return assignees;
  }, [taskSet, staff, meProfile]);

  return (
    <Stack direction="row" alignItems="center" gap={0.75}>
      <Typography level="body-sm">Assignee: </Typography>
      <Select
        size="sm"
        onChange={(_event, value) => {
          if (value === "") {
            setSelectedAssignee(null);
          } else {
            setSelectedAssignee(value);
          }
        }}
        value={selectedAssignee ?? ""}
        renderValue={(value) =>
          value?.value ? (
            <StaffItem
              nameOverride={meProfile?.uuid === value.value ? "Me" : undefined}
              staffInfo={{ user: assigneeChoices.get(value.value) }}
              showName
            />
          ) : (
            "Anyone"
          )
        }
      >
        <Option value="">Anyone</Option>
        {Array.from(assigneeChoices.entries(), ([_, assignee]) => (
          <Option key={assignee.uuid} value={assignee.uuid}>
            <StaffItem
              staffInfo={{ user: assignee }}
              nameOverride={meProfile?.uuid === assignee.uuid ? "Me" : undefined}
            />
          </Option>
        ))}
      </Select>
    </Stack>
  );
};

const ViewBySwitcher = () => {
  const { viewBy, setViewBy } = useTaskSetOverview();

  return (
    <Stack direction="row" gap={0.75} alignItems="center">
      <Typography level="body-sm">View:</Typography>
      {/* work around another mui toggle button corner radius issue */}
      <ToggleButtonGroup
        size="sm"
        value={viewBy}
        onChange={(_, value) => value && setViewBy(value)}
        sx={{
          ".MuiButton-root": {
            "&:first-of-type": {
              borderTopLeftRadius: "4px!important",
              borderBottomLeftRadius: "4px!important",
            },
            "&:last-of-type": {
              borderTopRightRadius: "4px!important",
              borderBottomRightRadius: "4px!important",
            },
          },
        }}
      >
        <Button value="grade">Grade</Button>
        <Button value="status">Status</Button>
      </ToggleButtonGroup>
    </Stack>
  );
};

export default function TaskSetOverviewMenu() {
  const { taskSet } = useTaskSetOverview();
  const formattedDate = taskSet
    ? format(parse(taskSet.startDate, "yyyy-MM-dd", new Date()), "do MMM")
    : undefined;
  const navigate = useNavigate();

  return (
    <Box>
      <Stack
        gap={2}
        direction="row"
        alignItems="center"
        sx={{
          padding: 1,
          borderBottom: "1px solid",
          borderBottomColor: "neutral.200",
          justifyContent: "flex-start",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
          <IconButtonLink
            to="/gym/$venueUuid/schedule/calendar"
            params={{ venueUuid: taskSet?.venue || "" }}
            search={taskSet ? { month: format(taskSet.startDate, "yyyy-MM") } : undefined}
            variant="soft"
            color="neutral"
            size="sm"
            sx={{ borderRadius: "100%" }}
          >
            <KeyboardArrowLeft />
          </IconButtonLink>
          <Typography
            level="title-lg"
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              flex: 1,
              minWidth: 0,
            }}
          >
            {!taskSet ? (
              <Skeleton>XXXXXXXXX</Skeleton>
            ) : taskSet.name ? (
              `${taskSet.name}(${formattedDate})`
            ) : (
              formattedDate
            )}
          </Typography>
        </Stack>
        <Dropdown>
          {/* data-last-child here is a hack to make sure the radiuses work */}
          <MenuButton
            data-last-child
            slots={{ root: IconButton }}
            slotProps={{
              root: {
                size: "sm",
                variant: "soft",
                sx: { borderRadius: "100%" },
              },
            }}
          >
            <Settings sx={{ color: "neutral.900" }} />
          </MenuButton>
          <Menu size="sm">
            {taskSet && (
              <TaskSetMenuItems
                taskSet={taskSet}
                onEdit={() => {
                  navigate({
                    to: "/gym/$venueUuid/schedule/set/$setUuid/edit",
                    params: { venueUuid: taskSet.venue, setUuid: taskSet.uuid },
                  });
                }}
              />
            )}
          </Menu>
        </Dropdown>
        <Box sx={{ flex: 1 }} />
        <ViewBySwitcher />
        <AssigneeSelector />
      </Stack>
    </Box>
  );
}
